import type { TOC } from '@ember/component/template-only';
import { array } from '@ember/helper';
import {
  PrismLoadingBar,
  PrismModal,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import { containerQuery } from 'ember-container-query';
import { t } from 'ember-intl';
import GeneralButtonsButtonExport from 'lumen/components/general/buttons/button-export';
import GeneralModalFilterBar from 'lumen/components/general/modal-filter-bar';
import GeneralModalsModalHeaderDateSelection from 'lumen/components/general/modals/modal-header-date-selection';
import ParityChartsChartTrends from 'lumen/components/parity/charts/chart-trends';
import type MarketParityInsightTrendsController from 'lumen/controllers/market/parity/insight/trends';
import getElement from 'lumen/modifiers/get-element';
import { PARITY_FILTER } from 'lumen/utils/constants/pi-constant-values';

interface MarketParityInsightTrendsSignature {
  Args: {
    controller: MarketParityInsightTrendsController;
    model: unknown;
  };
}

<template>
  <PrismModal
    @onClose={{@controller.redirectOnClose}}
    @title={{@controller.formattedPeriod}}
  >
    <:navigation>
      <GeneralModalsModalHeaderDateSelection
        @beginReached={{@controller.beginReached}}
        @changeDayAction={{@controller.changeExtractDate}}
        @endReached={{@controller.endReached}}
      />
    </:navigation>

    <:actions>
      <GeneralButtonsButtonExport
        @exportAction={{@controller.exportTrends}}
        @isDisabled={{@controller.isExportButtonDisabled}}
      />
    </:actions>

    <:default>
      <div
        class="modal--stretched overflow-hidden"
        {{containerQuery onQuery=@controller.onResizeModal}}
      >
        <PrismTabsContainer
          @activeTabId={{@controller.selection}}
          @onSelectTab={{@controller.changeSelection}}
          @visibleTabIds={{array
            PARITY_FILTER.LOSS
            PARITY_FILTER.MEET
            PARITY_FILTER.WIN
          }}
          as |TabsContainer|
        >
          <TabsContainer.header
            {{getElement @controller.saveTabsContainerHeaderHeight}}
          >
            <:default as |Header|>
              <Header.tab @id={{PARITY_FILTER.LOSS}}>
                {{t "parity_analytics.loss"}}
              </Header.tab>
              <Header.tab @id={{PARITY_FILTER.MEET}}>
                {{t "parity_analytics.meet"}}
              </Header.tab>
              <Header.tab @id={{PARITY_FILTER.WIN}}>
                {{t "parity_analytics.win"}}
              </Header.tab>
            </:default>
          </TabsContainer.header>

          <TabsContainer.content>
            <GeneralModalFilterBar
              class="truncate"
              title={{@controller.filterString}}
              {{getElement @controller.saveFilterBarHeight}}
            >
              {{@controller.filterString}}
            </GeneralModalFilterBar>

            {{#if @controller.isLoading}}
              <PrismLoadingBar class="relative" />
            {{/if}}

            <div>
              <ParityChartsChartTrends
                @data={{@controller.trendsData}}
                @dateAggregation={{@controller.dateAggregation}}
                @height={{@controller.chartHeight}}
                @isLoading={{@controller.isLoading}}
                @segmentation={{@controller.segmentation}}
                @sortType={{@controller.sortType}}
                @winMeetOrLoss={{@controller.selection}}
              />
            </div>
          </TabsContainer.content>
        </PrismTabsContainer>
      </div>
    </:default>
  </PrismModal>
</template> satisfies TOC<MarketParityInsightTrendsSignature>;
